import type { ProfileAnswers } from "../domain/answers";
import type { FlowAnswers, StepId } from "./steps";
import { PHOTO_MAX, PHOTO_MIN, TEXT_MIN, isScale, rulesAccepted, scalesComplete } from "./steps";

export type TranslateFn = (key: string, vars?: Record<string, string | number>) => string;

/**
 * One short sentence saying what to do next when Continue is locked.
 * Never a bare "Invalid".
 */
export function proceedHint(
  stepId: StepId,
  answers: FlowAnswers,
  profile: ProfileAnswers,
  t: TranslateFn
): string | null {
  switch (stepId) {
    case "welcome":
    case "verifyIntro":
    case "verifyReview":
    case "setupIntro":
    case "ready":
      return null;

    case "faithConfirm":
      if (answers.isChristian === false) {
        return t("faith.hintNotChristian");
      }
      if (answers.isChristian !== true) {
        return t("faith.hintUnset");
      }
      return null;

    case "communityRules":
      if (!rulesAccepted(answers)) {
        return t("rules.hint");
      }
      return null;

    case "intent":
      if (!answers.intent) return t("intent.hint");
      return null;

    case "account":
      if (!answers.email.includes("@")) return t("account.hintEmail");
      if (answers.password.length < 8) return t("account.hintPassword");
      return null;

    case "verifyId":
      return t("verify.id.hint");

    case "verifyVideo":
      return t("verify.video.hint");

    case "verifyPhotos":
      return t("verify.photos.hint", { min: PHOTO_MIN, max: PHOTO_MAX });

    case "whyChristian":
      if (answers.whyChristian.trim().length < TEXT_MIN.whyChristian) {
        return t("setup.whyChristian.hint", { min: TEXT_MIN.whyChristian });
      }
      return null;

    case "valueScales":
      if (!scalesComplete(answers)) return t("scales.hint");
      return null;

    case "aboutYou":
      if (answers.aboutYou.trim().length < TEXT_MIN.aboutYou) {
        return t("about.hint", { min: TEXT_MIN.aboutYou });
      }
      return null;

    case "lookingFor":
      if (answers.lookingFor.trim().length < TEXT_MIN.lookingFor) {
        return t("looking.hint", { min: TEXT_MIN.lookingFor });
      }
      return null;

    case "loveReady":
      if (answers.loveReady === false) {
        return t("loveReady.hintNo");
      }
      if (answers.loveReady !== true) return t("loveReady.hintUnset");
      return null;

    case "lifestyle":
      if (answers.hobbies.trim().length < TEXT_MIN.hobbies) {
        return t("lifestyle.hintHobbies");
      }
      if (answers.profession.trim().length < TEXT_MIN.profession) {
        return t("lifestyle.hintProfession");
      }
      return null;

    case "faithDepth": {
      const f = profile.faith;
      if (f.servingImportance == null) return t("faithDepth.hintServing");
      if (f.keepsCommandments == null) return t("faithDepth.hintCommandments");
      if (f.grewUpInChurch == null) return t("faithDepth.hintGrewUp");
      if (f.churchAttendance == null) return t("faithDepth.hintAttendance");
      if (f.spreadsGospel == null) return t("faithDepth.hintGospel");
      if (f.hasIdols == null) return t("faithDepth.hintIdols");
      return null;
    }

    case "character": {
      const c = profile.character;
      if (c.peacemaker == null) return t("character.hintPeacemaker");
      if (c.homebuilder == null) return t("character.hintHomebuilder");
      if (c.humorous == null) return t("character.hintHumorous");
      if (c.caringAffectionate == null) return t("character.hintCaring");
      if (c.wantsPeacefulHome == null) return t("character.hintPeaceful");
      if (c.goodManners == null) return t("character.hintManners");
      if (!isScale(c.honestyRating)) return t("character.hintHonesty");
      return null;
    }

    case "history": {
      const h = profile.history;
      if (h.usedMarijuana == null) return t("history.hintStart");
      if (h.usedLsd == null) return t("history.hintLsd");
      if (h.usedOtherDrugs == null) return t("history.hintOtherDrugs");
      if (h.wasAddicted == null) return t("history.hintAddicted");
      if (h.stableLovingChildhood == null) return t("history.hintChildhood");
      if (h.carriesScars == null) return t("history.hintScars");
      if (h.readyForLastingRelationship == null) {
        return t("history.hintLasting");
      }
      return null;
    }

    case "appearance":
      if (profile.appearance.hasTattoos == null) return t("appearance.hintHas");
      if (profile.appearance.acceptsPartnerTattoos == null) {
        return t("appearance.hintAccepts");
      }
      return null;

    case "homeAndLand": {
      const h = profile.home;
      if (h.herbKnowledge == null) return t("home.hintStart");
      if (h.plantsOwnFruitVeg == null) return t("home.hintPlant");
      if (h.interestedInFruitForest == null) return t("home.hintFruitForest");
      if (h.preppingForCalamities == null) return t("home.hintPrepping");
      if (h.communityFarming == null) return t("home.hintCommunity");
      if (h.canningAndPreserving == null) return t("home.hintCanning");
      if (h.survivalSkills == null) return t("home.hintSurvival");
      return null;
    }

    case "family":
      if (profile.children == null) return t("family.hintChildren");
      if (profile.requiredPartnerFaith == null) {
        return t("family.hintFaith");
      }
      if (profile.acceptsPastSubstanceUse == null) {
        return t("family.hintSubstances");
      }
      return null;

    case "ancestry":
      if (profile.ancestry.own.length === 0) {
        return t("ancestry.hintOwn");
      }
      if (profile.ancestry.accepted.length === 0) {
        return t("ancestry.hintAccepted");
      }
      return null;

    case "interests":
      if (profile.interests.length === 0) return t("interests.hint");
      return null;

    case "reflection": {
      const f = profile.free;
      if (
        f.describeYourself.trim().length < TEXT_MIN.describeYourself &&
        answers.aboutYou.trim().length < TEXT_MIN.aboutYou
      ) {
        return t("about.hint", { min: TEXT_MIN.aboutYou });
      }
      if (f.lifePurpose.trim().length < TEXT_MIN.lifePurpose) {
        return t("reflection.hintPurpose", { min: TEXT_MIN.lifePurpose });
      }
      if (f.fifteenYearVision.trim().length < TEXT_MIN.fifteenYearVision) {
        return t("reflection.hintVision", { min: TEXT_MIN.fifteenYearVision });
      }
      if (f.weaknesses.trim().length < TEXT_MIN.weaknesses) {
        return t("reflection.hintWeaknesses", { min: TEXT_MIN.weaknesses });
      }
      return null;
    }

    case "profile":
      if (answers.firstName.trim().length < TEXT_MIN.firstName) {
        return t("profile.hintName");
      }
      if (!(Number(answers.age) >= 18)) return t("profile.hintAge");
      if (answers.city.trim().length < TEXT_MIN.city) return t("profile.hintCity");
      if (answers.gender !== "male" && answers.gender !== "female") {
        return t("profile.hintGender");
      }
      return null;

    default:
      return null;
  }
}
